import { useMemo, useCallback } from "react";

import {
    AbfLoanExpanded,
    getZcLedger,
    useBestQuotes,
    getCollateralMintToInfo,
    useStrategyDurations,
    useMaxQuotesForCollateral,
    useOraclePrices,
    PriceFetchType,
    Ledger,
    BestQuote,
    LedgerExpanded,
    CollateralWithMaxQuote,
    summarizeLedgerAccount,
    parseCollateral,
    isStrategyDurationSame,
    StrategyDuration,
    RefinanceOption,
    AdditionalPrincipalLimit,
    useLoopscaleWalletContext,
    useIncreasePrincipalTransaction,
    IncreaseCreditTransactionParams,
    getZcCollateral,
    getZcLoanLiquidationThreshold
} from "@bridgesplit/abf-react";
import {
    bsMath,
    uiAmountToLamports,
    roundDownToDecimals,
    decimalsToBps,
    LOADING_ERROR,
    Result,
    MISSING_PARAM_ERROR
} from "@bridgesplit/utils";
import { getDurationIndex, IncreaseLeverageTransactionInput } from "@bridgesplit/abf-sdk";
import { useTransactionSender } from "app/components/transactions";
import { TrackTransactionEvent } from "app/types";

import { calculateOptionsWithOffsets } from "./utils";
import { IncreasePrincipalQuotes } from "./types";

export function useIncreasePrincipal({
    loanExpanded,
    strategyDurations,
    additionalPrincipal,
    selectedOption,
    additionalCollateral
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    strategyDurations?: StrategyDuration[];
    additionalPrincipal?: number | undefined;
    selectedOption?: RefinanceOption | undefined;
    additionalCollateral?: number | undefined;
}): IncreasePrincipalQuotes {
    // ===== Loan Data =====
    const firstLedger = getZcLedger(loanExpanded);
    const totalOutstanding = firstLedger?.ledgerDebt.total ?? 0;
    const newPrincipalAmount = totalOutstanding + (additionalPrincipal ?? 0);

    // ===== Available Options (for dropdown) =====
    const {
        validOptions,
        option,
        optionsWithOffsets,
        isLoading: isLoadingBestQuotes,
        isFetching: isFetchingBestQuotes
    } = useAvailableOptions({
        loanExpanded,
        firstLedger,
        currentPrincipalAmount: totalOutstanding,
        newPrincipalAmount,
        selectedOption,
        additionalPrincipal,
        strategyDurations,
        additionalCollateral
    });

    // ===== Max Borrow Calculation =====
    const {
        additionalPrincipalLimit,
        maxBorrowAlert,
        isLoading: isLoadingMaxBorrowLimit,
        isFetching: isFetchingMaxBorrowLimit
    } = useMaxBorrowLimit({
        loanExpanded,
        firstLedger,
        option,
        additionalPrincipal,
        additionalCollateral
    });

    // ===== Refinance Check =====
    const isRefinance = useMemo(() => checkIsRefinanceNeeded(firstLedger, option), [firstLedger, option]);
    // todo -if true we get fucked on manual so fix it

    // ===== Calculated Values =====
    const newBorrowApy = option?.bestQuote?.apy ?? 0;
    const weightedTotalApy = calculateNewWeightedApy(newBorrowApy, additionalPrincipal ?? 0, firstLedger);

    return {
        option,
        validOptions,
        optionsWithOffsets,
        additionalPrincipalLimit,
        maxBorrowAlert,
        newBorrowApy,
        weightedTotalApy,
        isRefinance,
        loanExpanded,
        isLoadingBestQuotes,
        isFetchingBestQuotes,
        isLoadingMaxBorrowLimit: isLoadingMaxBorrowLimit,
        isFetchingMaxBorrowLimit: isFetchingMaxBorrowLimit
    };
}

// ===== Internal Hooks =====

// Backend contract: minPrincipalAmount = current payoff, principalRepayment = outstanding principal,
// additionalBorrowAmount = the extra. Self-refi needs `payoff + additional - principalRepayment` of headroom.
function buildRefinanceParams({
    firstLedger,
    additionalBorrow,
    loanExpanded
}: {
    firstLedger: LedgerExpanded | undefined;
    additionalBorrow: number | undefined;
    loanExpanded: AbfLoanExpanded | undefined;
}) {
    return {
        refinanceStrategy: firstLedger?.strategy ?? "",
        principalRepayment: uiAmountToLamports(
            summarizeLedgerAccount(firstLedger).principalOutstanding,
            loanExpanded?.principalMetadata.decimals
        ),
        minLqt: decimalsToBps(getZcLoanLiquidationThreshold(loanExpanded) ?? 0),
        additionalBorrowAmount: uiAmountToLamports(additionalBorrow, loanExpanded?.principalMetadata.decimals),
        ledgerIndex: 0
    };
}

function useAdditionalCollateralQuoteInputs({
    loanExpanded,
    additionalCollateral,
    collateral
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    additionalCollateral: number | undefined;
    collateral?: ReturnType<typeof parseCollateral>[];
}) {
    return useMemo(() => {
        const collateralMint = getZcCollateral(loanExpanded)?.metadata.mint;
        if (!additionalCollateral || additionalCollateral <= 0 || !collateralMint) {
            return { byMint: undefined, augmentedCollateral: collateral };
        }

        const byMint = new Map([[collateralMint, additionalCollateral]]);
        const augmentedCollateral = collateral?.map((asset) => {
            if (asset.metadata.mint !== collateralMint) return asset;

            const amount = asset.amount + additionalCollateral;
            return { ...asset, amount, usdValue: amount * (asset.usdPrice ?? 0) };
        });

        return { byMint, augmentedCollateral };
    }, [loanExpanded, collateral, additionalCollateral]);
}

// Helper to fetch best quotes for a specific amount
function useBestQuotesForAmount({
    loanExpanded,
    firstLedger,
    additionalBorrow,
    additionalCollateral,
    strategyDurations,
    skip
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    firstLedger: LedgerExpanded | undefined;
    additionalBorrow: number | undefined;
    additionalCollateral: number | undefined;
    strategyDurations: StrategyDuration[] | undefined;
    skip?: boolean;
}) {
    const durations = useMemo(() => {
        return Array.isArray(strategyDurations) ? strategyDurations : strategyDurations ? [strategyDurations] : [];
    }, [strategyDurations]);

    const refinanceParams = useMemo(() => {
        return buildRefinanceParams({
            firstLedger,
            additionalBorrow,
            loanExpanded
        });
    }, [firstLedger, additionalBorrow, loanExpanded]);

    const { byMint: additionalCollateralByMint } = useAdditionalCollateralQuoteInputs({
        loanExpanded,
        additionalCollateral
    });
    const collateralMintToInfo = useMemo(
        () => getCollateralMintToInfo(loanExpanded, additionalCollateralByMint),
        [loanExpanded, additionalCollateralByMint]
    );

    return useBestQuotes({
        collateralMintToInfo,
        principalMint: firstLedger?.principalMint,
        strategyDurations: durations,
        showSelf: false,
        skip: skip || !additionalBorrow || !loanExpanded || !firstLedger,
        minPrincipalAmountLamports: uiAmountToLamports(
            firstLedger?.ledgerDebt.total,
            loanExpanded?.principalMetadata.decimals
        ),
        loanAddress: loanExpanded?.loan.address,
        refinanceParams
    });
}

function useAvailableOptions({
    loanExpanded,
    firstLedger,
    currentPrincipalAmount,
    newPrincipalAmount,
    selectedOption,
    additionalPrincipal,
    additionalCollateral,
    strategyDurations: externalStrategyDurations
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    firstLedger: LedgerExpanded | undefined;
    currentPrincipalAmount: number;
    newPrincipalAmount: number;
    selectedOption: RefinanceOption | undefined;
    additionalPrincipal: number | undefined;
    additionalCollateral: number | undefined;
    strategyDurations?: StrategyDuration[];
}) {
    const { strategyDurations: hookStrategyDurations } = useStrategyDurations();

    // Use external if provided, otherwise fall back to hook
    const strategyDurations = externalStrategyDurations ?? hookStrategyDurations;

    const refinanceParams = buildRefinanceParams({
        firstLedger,
        additionalBorrow: additionalPrincipal,
        loanExpanded
    });

    const shouldSkipBestQuotes = !newPrincipalAmount || !loanExpanded || !firstLedger;
    const { byMint: additionalCollateralByMint } = useAdditionalCollateralQuoteInputs({
        loanExpanded,
        additionalCollateral
    });
    const collateralMintToInfo = useMemo(
        () => getCollateralMintToInfo(loanExpanded, additionalCollateralByMint),
        [loanExpanded, additionalCollateralByMint]
    );

    const {
        data: quotes,
        isLoading,
        isFetching
    } = useBestQuotes({
        collateralMintToInfo,
        principalMint: firstLedger?.principalMint,
        strategyDurations,
        showSelf: false,
        skip: shouldSkipBestQuotes,
        minPrincipalAmountLamports: uiAmountToLamports(
            currentPrincipalAmount,
            loanExpanded?.principalMetadata.decimals
        ),
        loanAddress: loanExpanded?.loan.address,
        refinanceParams
    });

    const optionsWithOffsets = calculateOptionsWithOffsets({
        quotes,
        strategyDurations,
        newPrincipalAmount,
        loanExpanded
    });

    const validOptions = optionsWithOffsets?.filter((o) => !!o.bestQuote);
    const option = selectedOption ?? validOptions?.[0];

    return { validOptions, option, optionsWithOffsets, isLoading, isFetching };
}

/**
 * Calculates the maximum borrow limit with accurate LTV
 *
 * Flow:
 * 1. Get max quotes for selected duration (max borrowable amount)
 * 2. Calculate preliminary limit from max quotes
 * 3. Fetch best quote AT that max amount to get accurate LTV
 * 4. Recalculate limit using the best quote's LTV (matches what will be submitted)
 *
 * This ensures the limit stays stable (only changes with duration) while using
 * the correct LTV that the program would actually enforce.
 */
function useMaxBorrowLimit({
    loanExpanded,
    firstLedger,
    option,
    additionalPrincipal,
    additionalCollateral
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    firstLedger: LedgerExpanded | undefined;
    option: RefinanceOption | undefined;
    additionalPrincipal: number | undefined;
    additionalCollateral?: number | undefined;
}) {
    const { getOracle } = useOraclePrices(
        [firstLedger?.principalMint, ...(loanExpanded?.collateral.map((c) => c.metadata.mint) ?? [])],
        PriceFetchType.Twap
    );

    const parsedLoanCollateral = useMemo(
        () => loanExpanded?.collateral.map((collateral) => parseCollateral(collateral, getOracle)) ?? [],
        [getOracle, loanExpanded?.collateral]
    );
    const { augmentedCollateral: actualQuoteCollateral } = useAdditionalCollateralQuoteInputs({
        loanExpanded,
        additionalCollateral,
        collateral: parsedLoanCollateral
    });
    const firstLedgerStrategy = firstLedger?.strategy;
    const firstLedgerDebtTotal = firstLedger?.ledgerDebt.total;

    // Build strategy credit from existing loan debt so the max quote accounts for
    // capacity already allocated to this loan's strategy
    const strategyCredit = useMemo(() => {
        if (!firstLedgerStrategy || !firstLedgerDebtTotal) return undefined;
        return new Map([[firstLedgerStrategy, firstLedgerDebtTotal]]);
    }, [firstLedgerStrategy, firstLedgerDebtTotal]);

    // Step 1: Get max quotes (max borrowable amount for selected duration)
    const {
        data: maxQuotes,
        isLoading: isLoadingMaxQuotes,
        isFetching: isFetchingMaxQuotes
    } = useMaxQuotesForCollateral({
        collateral: actualQuoteCollateral,
        principalMint: firstLedger?.principalMint,
        strategyDuration: option?.strategyDuration,
        skip: !option,
        strategyCredit
    });

    // Step 2: Calculate preliminary limits to get max borrow amount
    const preliminaryLimits = useMemo(() => {
        return calculateAdditionalBorrowLimits({
            loanExpanded,
            firstLedger,
            maxQuotes,
            additionalCollateral
        });
    }, [maxQuotes, loanExpanded, firstLedger, additionalCollateral]);

    // Step 3: Calculate total principal at max
    const maxPrincipalAmount = useMemo(() => {
        if (!preliminaryLimits || !firstLedger) return undefined;
        return firstLedger.ledgerDebt.total + preliminaryLimits.additionalBorrowLimit;
    }, [preliminaryLimits, firstLedger]);

    // Step 4: Get best quote AT MAX AMOUNT (for accurate LTV used in submission)
    const {
        data: bestQuotesAtMax,
        isLoading: isLoadingBestQuotesAtMax,
        isFetching: isFetchingBestQuotesAtMax
    } = useBestQuotesForAmount({
        loanExpanded,
        firstLedger,
        additionalBorrow: preliminaryLimits?.additionalBorrowLimit,
        additionalCollateral,
        strategyDurations: option?.strategyDuration ? [option.strategyDuration] : undefined,
        skip: !maxPrincipalAmount || !option
    });

    // Step 5: Recalculate limits using the best quote at max's LTV
    const additionalPrincipalLimit = useMemo(() => {
        if (!bestQuotesAtMax || !option) return preliminaryLimits;

        const bestQuoteAtMax = bestQuotesAtMax.find((q) => isStrategyDurationSame(q, option.strategyDuration));
        if (!bestQuoteAtMax) return preliminaryLimits;

        return calculateAdditionalBorrowLimits({
            loanExpanded,
            firstLedger,
            maxQuotes,
            overrideLtv: bestQuoteAtMax.ltv,
            additionalCollateral
        });
    }, [bestQuotesAtMax, option, preliminaryLimits, loanExpanded, firstLedger, maxQuotes, additionalCollateral]);

    const maxBorrowAlert = useMemo(() => {
        if (!additionalPrincipalLimit || !additionalPrincipal) return undefined;

        if (additionalPrincipalLimit.additionalBorrowLimit < additionalPrincipal) {
            if (additionalPrincipalLimit.additionalBorrowLimitFromQuote < additionalPrincipal) {
                return "There are not enough available funds to borrow this amount";
            }

            if (
                additionalPrincipalLimit.maxAdditionalBorrowFromLtv <
                roundDownToDecimals(additionalPrincipal, loanExpanded?.principalMetadata.decimals ?? 0)
            ) {
                return "You do not have enough collateral to borrow this amount";
            }
        }
        return undefined;
    }, [additionalPrincipalLimit, additionalPrincipal, loanExpanded]);

    const isLoading = isLoadingMaxQuotes || isLoadingBestQuotesAtMax;
    const isFetching = isFetchingMaxQuotes || isFetchingBestQuotesAtMax;

    return {
        additionalPrincipalLimit,
        maxBorrowAlert,
        isLoading: isLoading,
        isFetching
    };
}

function checkIsRefinanceNeeded(
    firstLedger: Ledger | undefined,
    option: { bestQuote: BestQuote | undefined } | undefined
): boolean {
    if (!firstLedger || !option?.bestQuote) return false;

    if (firstLedger.strategy !== option.bestQuote.lendingStrategyAddress) return true;

    const currentDuration = {
        duration: firstLedger.duration,
        durationType: firstLedger.durationType
    };
    const newDuration = {
        duration: option.bestQuote.duration,
        durationType: option.bestQuote.durationType
    };
    return !isStrategyDurationSame(currentDuration, newDuration);
}

function calculateAdditionalBorrowLimits({
    loanExpanded,
    firstLedger,
    maxQuotes,
    overrideLtv,
    additionalCollateral
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    firstLedger: LedgerExpanded | undefined;
    maxQuotes: CollateralWithMaxQuote[] | undefined;
    overrideLtv?: number;
    additionalCollateral?: number;
}):
    | {
          additionalBorrowLimit: number;
          additionalBorrowLimitFromQuote: number;
          maxAdditionalBorrowFromLtv: number;
      }
    | undefined {
    if (!maxQuotes || !loanExpanded || !firstLedger || !loanExpanded.collateral.length) return undefined;

    // Extract LTV from maxQuotes, or use override if provided
    const relevantMaxQuotes = maxQuotes.filter((q) =>
        loanExpanded.collateral.some((collateral) => collateral.metadata.mint === q.metadata.mint)
    );
    const relevantMaxQuote = relevantMaxQuotes.find((q) => q.maxQuote)?.maxQuote;

    if (!relevantMaxQuote) return undefined;

    const newLtv = overrideLtv ?? relevantMaxQuote.ltv;

    const getAggregateMaxBorrow = (quotes: CollateralWithMaxQuote[]) => {
        const maxBorrowFromCollateral = quotes.reduce((sum, q) => sum + (q.maxQuote?.maxBorrow ?? 0), 0);
        const principalAvailable = Math.max(...quotes.map((q) => q.maxQuote?.principalAvailable ?? 0), 0);
        return Math.min(maxBorrowFromCollateral, principalAvailable);
    };

    const newStratMax = getAggregateMaxBorrow(
        relevantMaxQuotes.filter((q) => q.maxQuote?.lendingStrategyAddress !== firstLedger.strategy)
    );

    const currentStratMax =
        getAggregateMaxBorrow(
            relevantMaxQuotes.filter((q) => q.maxQuote?.lendingStrategyAddress === firstLedger.strategy)
        ) + firstLedger?.ledgerDebt.total;

    const additionalBorrowLimitFromQuote =
        newStratMax > currentStratMax
            ? Math.max(
                  roundDownToDecimals(
                      bsMath.sub(newStratMax, firstLedger?.ledgerDebt.total) ?? 0, // removed FE buffer was previously (bsMath.sub(newStratMax, firstLedger?.ledgerDebt.total) ?? 0) * (1 - .01)
                      loanExpanded?.principalMetadata.decimals ?? 0
                  ),
                  0
              )
            : roundDownToDecimals(
                  bsMath.sub(currentStratMax, firstLedger?.ledgerDebt.total) ?? 0,
                  loanExpanded?.principalMetadata.decimals ?? 0
              );

    // Calculate total collateral value including additional collateral being added
    const existingCollateralValue = loanExpanded.collateral.reduce(
        (total, collateral) => total + collateral.amount * (collateral.usdPrice ?? 0),
        0
    );
    const firstCollateral = loanExpanded.collateral[0];
    const additionalCollateralValue = (additionalCollateral ?? 0) * (firstCollateral?.usdPrice ?? 0);
    const totalCollateralValue = existingCollateralValue + additionalCollateralValue;

    const maxPrincipalUsd = bsMath.mul(totalCollateralValue, newLtv);
    const principalPrice = loanExpanded?.principalUsdPrice;
    if (principalPrice === undefined) return undefined;
    const maxPrincipalAmount = bsMath.div(maxPrincipalUsd, principalPrice);

    if (maxPrincipalAmount === undefined) return undefined;

    const maxAdditionalBorrowFromLtv = Math.max(
        roundDownToDecimals(
            bsMath.sub(maxPrincipalAmount, firstLedger.ledgerDebt.total) ?? 0,
            loanExpanded?.principalMetadata.decimals ?? 0
        ),
        0
    );
    const additionalBorrowLimit = Math.min(additionalBorrowLimitFromQuote, maxAdditionalBorrowFromLtv);

    return { additionalBorrowLimit, additionalBorrowLimitFromQuote, maxAdditionalBorrowFromLtv };
}

function calculateNewWeightedApy(newApy: number, newBorrow: number, firstLedger: LedgerExpanded | undefined): number {
    const { principalOutstanding: currentBalance, apy: currentApy } = summarizeLedgerAccount(firstLedger);

    const totalPrincipal = currentBalance + newBorrow;
    if (totalPrincipal === 0) {
        return newApy;
    }

    const averageApy = (currentApy * currentBalance + newApy * newBorrow) / totalPrincipal;

    return averageApy;
}

export function useSubmitIncreasePrincipal({
    loanExpanded,
    option,
    additionalPrincipal,
    additionalPrincipalLimit,
    isRefinance,
    transactionOptions
}: {
    loanExpanded: AbfLoanExpanded | undefined;
    option: RefinanceOption | undefined;
    additionalPrincipal: number | undefined;
    additionalPrincipalLimit: AdditionalPrincipalLimit | undefined;
    isRefinance: boolean | undefined;
    transactionOptions?: { preventAutoDialogClose?: boolean };
}): {
    submit: () => Promise<Result<unknown>>;
    increasePrincipalParams: IncreaseLeverageTransactionInput | undefined;
} {
    const send = useTransactionSender();
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    const increasePrincipal = useIncreasePrincipalTransaction();

    const params = useMemo(() => {
        const firstLedger = getZcLedger(loanExpanded);
        if (
            !loanExpanded ||
            !firstLedger ||
            !option?.bestQuote ||
            !option.bestQuote.lendingStrategyAddress ||
            isRefinance === undefined ||
            !additionalPrincipal
        ) {
            return null;
        }

        const durationIndex = getDurationIndex({
            duration: option.bestQuote.duration,
            durationType: option.bestQuote.durationType
        });
        if (durationIndex === undefined) return null;

        const expectedApy = isRefinance ? option.bestQuote.apy : firstLedger.apy;
        const expectedLqt: [number, number, number, number, number] = isRefinance
            ? [option.bestQuote.liquidationThreshold, 0, 0, 0, 0]
            : firstLedger.lqtRatios;
        const strategy = isRefinance ? option.bestQuote.lendingStrategyAddress : firstLedger.strategy;

        const creditParams: IncreaseCreditTransactionParams = {
            decimals: loanExpanded.principalMetadata?.decimals ?? 0,
            loan: loanExpanded.loan.address,
            borrowParams: {
                amount: additionalPrincipal,
                duration: durationIndex,
                expectedLoanValues: { expectedApy, expectedLqt }
            },
            strategy
        };
        if (isRefinance) {
            creditParams.refinanceParams = {
                newStrategy: option.bestQuote.lendingStrategyAddress,
                ledgerIndex: 0,
                durationIndex
            };
        }

        const leverageParams: IncreaseLeverageTransactionInput = {
            loanAddress: loanExpanded.loan.address,
            principalRequested: [
                {
                    ledgerIndex: 0,
                    principalAmount: additionalPrincipal,
                    principalMint: loanExpanded.principalMetadata.mint,
                    strategy,
                    durationIndex,
                    expectedLoanValues: { expectedApy, expectedLqt }
                }
            ],
            depositCollateral: [],
            unifySetup: false
        };
        if (isRefinance) {
            leverageParams.refinanceParams = {
                ledgerIndex: 0,
                durationIndex
            };
        }

        return { creditParams, leverageParams };
    }, [loanExpanded, option, additionalPrincipal, isRefinance]);

    const submit = useCallback(async () => {
        if (!activeWallet) return Result.errFromMessage(LOADING_ERROR);
        if (!additionalPrincipal || additionalPrincipalLimit === undefined)
            return Result.errFromMessage(MISSING_PARAM_ERROR);
        if (!params) return Result.errFromMessage(MISSING_PARAM_ERROR);

        return await send(increasePrincipal, params.creditParams, {
            description: "Borrowing more",
            mixpanelEvent: { key: TrackTransactionEvent.IncreasePrincipal, params: params.creditParams },
            ...transactionOptions
        });
    }, [
        activeWallet,
        additionalPrincipal,
        additionalPrincipalLimit,
        params,
        send,
        increasePrincipal,
        transactionOptions
    ]);

    return {
        submit,
        increasePrincipalParams: params?.leverageParams
    };
}
